import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDarkTheme } from "@/contexts/DarkTheme";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/helpers/helperFunctions";
import { Range } from "react-range";
import Image from "next/image";
import Loader from "../Loader";
import { useTranslation } from "react-i18next";
import { usePostMarketPlaceFilterAuthors } from "@/api/marketPlaceApi";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import TraderCard from "../blocks/common/TraderCard";

const circleStyles = {
  height: "13px",
  width: "13px",
  borderRadius: "50%",
  border: "2px solid black",
  backgroundColor: "#fff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const lineStyling = {
  height: "5px",
  width: "96%",
  margin: "0 auto",
  borderRadius: "3px",
};
const dataSize = 20;

export default function AuthorsFilters({}: any) {
  const [filtersAuthors, setFiltersAuthors]: any = useState({
    sort_order: "win_p_desc",
    total_strategies: "",
    copiers: "",
    trading_profile: "",
    win_p: "",
  });
  const { darkTheme } = useDarkTheme();
  let [pageAuthors, setPageAuthors] = useState(1);
  let [totalDataLengthAuthors, setTotalDataLengthAuthors] = useState(0);
  let [dataAuthors, setDataAuthors] = useState([]);
  const [winningPercentage, setWinningPercentage] = useState<number>(0); // Initial range values
  const [totalStrategies, setTotalStrategies] = useState<number>(0); // Initial range values
  const [copiersCountSlider, setCopiersCountSlider] = useState<number>(0); // Initial range values
  const [isAnyFilterFilled, setIsAnyFilterFilled] = useState(false);
  const { t } = useTranslation("marketplace-page");
  const [authorFormData, setAuthorFormData] = useState<FormData | null>(null);
  const [searchInputValueByName, setSearchInputValueByName] = useState("");
  const {
    data: filterAuthorData,
    isLoading: loadingAuthors,
    isSuccess: isSuccessAuthor,
    refetch: refetchAuthorData,
  } = usePostMarketPlaceFilterAuthors(
    authorFormData,
    String(pageAuthors),
    String(dataSize),
  );

  useEffect(() => {
    const newFormData = new FormData();
    Object.keys(filtersAuthors).map((each) => {
      newFormData.append(each, filtersAuthors[each]);
    });
    setAuthorFormData(newFormData);
  }, [filtersAuthors]);

  useEffect(() => {
    const checkForFilters = Object.entries(filtersAuthors)
      .filter(([key]) => key !== "sort_order") // Exclude 'sort_order'
      .some(([, value]) => value !== ""); // Check if any remaining value is filled

    setIsAnyFilterFilled(checkForFilters);
  }, [filtersAuthors]);

  useEffect(() => {
    refetchAuthorData();
  }, [authorFormData]);

  useEffect(() => {
    if (isSuccessAuthor && filterAuthorData) {
      setDataAuthors(filterAuthorData.data.items);
      setTotalDataLengthAuthors(filterAuthorData.data.total);
    }
  }, [filterAuthorData, isSuccessAuthor]);

  const handleChange = (name: string, value: string) => {
    setFiltersAuthors((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const STEP = 1;
  const MIN = 0;
  const MAX = 10;

  const clearFilters = () => {
    setWinningPercentage(0);
    setCopiersCountSlider(0);
    setFiltersAuthors({
      sort_order: "",
      total_strategies: "",
      copiers: "",
      trading_profile: "",
      win_p: "",
      author_name: "",
    });
  };

  useEffect(() => {
    if (searchInputValueByName) {
      handleChange("author_name", searchInputValueByName);
    } else {
      handleChange("author_name", "");
    }
  }, [searchInputValueByName]);

  return (
    <>
      <div className="w-[100%]   sticky sm:w-[27%] h-full ">
        <Card className="w-[100%] h-auto  ">
          <CardHeader className="text-xl font-bold flex flex-row justify-between items-center">
            <div className="w-fit">{t("Filters")}</div>
            {isAnyFilterFilled && (
              <Image
                width={26}
                height={26}
                src="/images/marketplace/cross.png"
                alt="filter image"
                className={`mr-2 cursor-pointer ${
                  darkTheme ? "" : "marketplace-filter-icon-dark"
                }`}
                onClick={clearFilters}
              />
            )}
          </CardHeader>
          <CardContent>
            <Label>{t("Search by name")}</Label>
            <Input
              className="mt-2"
              value={searchInputValueByName}
              onChange={(e) => setSearchInputValueByName(e.target.value)}
            ></Input>
          </CardContent>

          <CardContent>
            <div className="mb-[8px]">{t("Trading Profile")}</div>
            <Select
              value={filtersAuthors.trading_profile}
              onValueChange={(value) => handleChange("trading_profile", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("Select")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={"Volatility"}>
                    {t("Volatility")}
                  </SelectItem>
                  <SelectItem value={"Overlap"}>{t("Overlap")}</SelectItem>
                  <SelectItem value={"Momentum"}>{t("Momentum")}</SelectItem>
                  <SelectItem value={"Volume"}>{t("Volume")}</SelectItem>
                  <SelectItem value={"Trend"}>{t("Trend")}</SelectItem>
                  <SelectItem value={"Statistics"}>
                    {t("Statistics")}
                  </SelectItem>
                  <SelectItem value={"Cycles"}>{t("Cycles")}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </CardContent>

          <CardContent>
            <div className="mb-[8px]">
              {t("Win Percentage")} ({winningPercentage}%)
            </div>

            <Range
              step={STEP}
              min={MIN}
              max={100}
              values={[winningPercentage]}
              onChange={(values) => {
                setWinningPercentage(values[0]);
                handleChange("win_p", String(values[0]));
              }}
              renderTrack={({ props, children }) => {
                const percent = ((winningPercentage - MIN) / (100 - MIN)) * 100;
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      ...lineStyling,
                      background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percent}%, hsl(var(--muted)) ${percent}%, hsl(var(--muted)) 100%)`,
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props, isDragged }) => {
                const { key, ...restProps } = props;
                return (
                  <div
                    key={key}
                    {...restProps}
                    style={{
                      ...props.style,
                      ...circleStyles,
                    }}
                  />
                );
              }}
            />
          </CardContent>
          <CardContent>
            <div className="mb-[8px]">
              {t("Copiers")} ({copiersCountSlider})
            </div>

            <Range
              step={STEP}
              min={MIN}
              max={MAX}
              values={[copiersCountSlider]}
              onChange={(values) => {
                setCopiersCountSlider(values[0]);
                handleChange("copiers", String(values[0]));
              }}
              renderTrack={({ props, children }) => {
                const percent =
                  ((copiersCountSlider - MIN) / (MAX - MIN)) * 100;

                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      ...lineStyling,
                      background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percent}%, hsl(var(--muted)) ${percent}%, hsl(var(--muted)) 100%)`,
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props, isDragged }) => {
                const { key, ...restProps } = props;

                return (
                  <div
                    key={key}
                    {...restProps}
                    style={{
                      ...props.style,
                      ...circleStyles,
                    }}
                  />
                );
              }}
            />
          </CardContent>

          <CardContent>
            <div className="mb-[8px]">
              {t("Total Strategies")} ({totalStrategies})
            </div>

            <Range
              step={STEP}
              min={MIN}
              max={100}
              values={[totalStrategies]}
              onChange={(values) => {
                setTotalStrategies(values[0]);
                handleChange("total_strategies", String(values[0]));
              }}
              renderTrack={({ props, children }) => {
                const percent = ((totalStrategies - MIN) / (100 - MIN)) * 100;
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      ...lineStyling,
                      background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percent}%, hsl(var(--muted)) ${percent}%, hsl(var(--muted)) 100%)`,
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props, isDragged }) => {
                const { key, ...restProps } = props;
                return (
                  <div
                    key={key}
                    {...restProps}
                    style={{
                      ...props.style,
                      ...circleStyles,
                    }}
                  />
                );
              }}
            />
          </CardContent>
        </Card>
      </div>
      <Card className="w-full relative  mt-5 sm:mt-0 sm:w-[71%] h-[70vh] overflow-auto ">
        <CardHeader className="flex  !p-3 px-6 justify-between flex-row items-center w-full">
          <CardTitle className="text-xl flex gap-2 items-center text-nowrap">
            {t("Top Traders")}
          </CardTitle>
          <div className="sortFilterMarketplace">
            <Select
              value={filtersAuthors.sort_order}
              onValueChange={(value) => {
                if (value == "none") {
                  value = "";
                }
                handleChange("sort_order", value);
              }}
            >
              <SelectTrigger className="w-[50px] outline-none cursor-pointer border-none bg-transparent text-lg ">
                <Image
                  width={22}
                  height={22}
                  src="/images/marketplace/filter.png"
                  alt="filter image"
                  className={`mr-2 cursor-pointer ${
                    darkTheme ? "" : "marketplace-filter-icon-dark"
                  }`}
                />
              </SelectTrigger>

              <SelectContent>
                <SelectGroup>
                  <SelectItem value={"win_p_desc"}>
                    {t("Highest Win Percentage")}
                  </SelectItem>
                  <SelectItem value={"copiers_desc"}>
                    {t("Highest Copiers Count")}
                  </SelectItem>
                  <SelectItem value={"total_strategies_desc"}>
                    {t("Highest Strategies Created")}
                  </SelectItem>
                  <SelectItem value={"total_trades_desc"}>
                    {t("Highest Trades Triggered")}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        {loadingAuthors || dataAuthors.length == 0 ? (
          !loadingAuthors ? (
            <div className="w-full flex items-center justify-center h-[200px]">
              {t("No data found")}
            </div>
          ) : (
            <div className="w-full flex items-center justify-center h-[200px]">
              <Loader />
            </div>
          )
        ) : (
          <CardContent className={`p-3 pt-0 w-full`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full justify-between">
              {dataAuthors.map((item: any, i: number) => {
                return <TraderCard item={item} key={item?.email}></TraderCard>;
              })}
            </div>
            <div className="mt-3 mb-10 sm:mb-0">
              <Pagination
                totalCount={totalDataLengthAuthors}
                currentPage={pageAuthors}
                changePage={setPageAuthors}
                limit={dataSize}
              />
            </div>
          </CardContent>
        )}
      </Card>
    </>
  );
}
